import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Tabs, Tab, Button, TextField, Alert } from "@mui/material";
import AuthContext from "../auth/AuthContext";
import { generateGameKey, getGameKey } from "../utils/apiClient";

const Profile = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [gameKey, setGameKey] = useState(null);
  const [gameKeyDisplay, setGameKeyDisplay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && currentTab === 0) {
      loadGameKey();
    }
  }, [user, currentTab]);

  const loadGameKey = async () => {
    try {
      const response = await getGameKey();
      setGameKey(response.game_key);
      // Mask the key for display (show first 20 chars and last 10 chars)
      if (response.game_key) {
        const masked = response.game_key.substring(0, 20) + "..." + response.game_key.substring(response.game_key.length - 10);
        setGameKeyDisplay(masked);
      }
    } catch (err) {
      console.error("Failed to load game key:", err);
    }
  };

  const handleGenerateGameKey = async () => {
    // Only show confirmation if a game key already exists
    if (gameKey && !window.confirm("Generating a new game key will overwrite your existing key. Continue?")) {
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await generateGameKey();
      setGameKey(response.game_key);
      setGameKeyDisplay(response.game_key); // Show full key after generation
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate game key");
      console.error("Failed to generate game key:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyGameKey = () => {
    if (gameKey) {
      navigator.clipboard.writeText(gameKey);
      alert("Game key copied to clipboard!");
    }
  };

  if (!user) return null;

  const handleTabChange = (event, newValue) => {
    const isAdmin = user?.is_admin;
    
    // Determine which action based on tab index
    if (isAdmin) {
      // Admin: 0=Information, 1=Management, 2=Logout
      if (newValue === 1) {
        navigate("/management");
        return;
      } else if (newValue === 2) {
        logout();
        navigate("/login");
        return;
      }
    } else {
      // Non-admin: 0=Information, 1=Logout
      if (newValue === 1) {
        logout();
        navigate("/login");
        return;
      }
    }
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h3" gutterBottom className="vintage-script-header">
        Profile
      </Typography>
      
      <Tabs
        className="management-tabs"
        value={currentTab}
        onChange={handleTabChange}
        sx={{ mb: 3 }}
      >
        <Tab label="Information" />
        {user?.is_admin && <Tab label="Management" />}
        <Tab label="Logout" />
      </Tabs>
      
      {currentTab === 0 && (
        <Paper sx={{
          p: 3,
          background: 'transparent',
          border: '2px solid var(--color-border-vintage)',
          borderRadius: 0,
          boxShadow: 'none',
          position: 'relative',
        }}>
          <Typography variant="body1" sx={{ mb: 2, fontFamily: 'var(--font-serif)' }}>
            <strong>Username:</strong> {user.username}
          </Typography>
          {user.email && (
            <Typography variant="body1" sx={{ mb: 2, fontFamily: 'var(--font-serif)' }}>
              <strong>Email:</strong> {user.email}
            </Typography>
          )}
          
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid var(--color-border-vintage)' }}>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: 'var(--font-serif)' }}>
              Game Key
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontFamily: 'var(--font-serif)', color: 'text.secondary' }}>
              Generate a game key to authenticate your Godot game with the API. You can only have one active key at a time.
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {gameKeyDisplay && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Your Game Key"
                  value={gameKeyDisplay}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleCopyGameKey}
                  sx={{ fontFamily: 'var(--font-serif)' }}
                >
                  Copy Key
                </Button>
              </Box>
            )}
            
            <Button
              variant="contained"
              onClick={handleGenerateGameKey}
              disabled={loading}
              sx={{ fontFamily: 'var(--font-serif)' }}
            >
              {gameKey ? "Regenerate Game Key" : "Generate Game Key"}
            </Button>
            
            {gameKey && (
              <Typography variant="body2" sx={{ mt: 2, fontFamily: 'var(--font-serif)', color: 'text.secondary', fontStyle: 'italic' }}>
                Warning: Generating a new key will invalidate your current key.
              </Typography>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Profile;


